import './ui/styles.css';
import './ui/extra.css';
import { applyLocally, newId, checkWorkbook, type Command, type HealthIssue } from '@pf/workbook';
import { compute } from './app/model.js';
import { missingSnapshots } from './app/investments-model.js';
import { loadCached, sync, type Loaded } from './app/sync.js';
import { queue, processQueue } from './app/queue.js';
import type { WorkbookSource } from './app/ports.js';
import { demoSource } from './adapters/demo-source.js';
import { sheetsSource } from './adapters/sheets-source.js';
import { sheetsApi } from './adapters/sheets-api.js';
import { api, ApiError, blockApi } from './adapters/api-client.js';
import { indexedDbCache } from './adapters/indexeddb-cache.js';
import { adoptServerPrefs } from './adapters/prefs.js';
import { setTricountBook, tricountApi, hideTricountBook } from './adapters/tricount-book.js';
import { $, esc, todayIso } from './ui/format.js';
import * as V from './views.js';
import * as E from './edit-views.js';
import { viewPlanner } from './planner.js';
import { viewPlannerWizard } from './planner-wizard.js';
import * as I from './investments-views.js';
import * as A from './account-views.js';
import * as T from './tricount-views.js';
import { viewTricountSettle } from './tricount-settle-views.js';
import * as M from './import-views.js';
import * as B from './bank-views.js';
import * as S from './shortcut-views.js';
import { viewAmazon } from './amazon-views.js';
import { viewAmazonCharge } from './amazon-charge-views.js';
import * as Y from './year-view.js';
import * as U from './users-views.js';
import * as O from './onboarding-views.js';
import * as C from './categories-admin-views.js';
import * as P from './planned-views.js';
import { clearChrome, fab } from './ui/feedback.js';
import { lastLoad, loadConcept } from './app/loads.js';
import { setFixedToday } from './app/clock.js';
import { recentCategories } from './app/category-suggest.js';
import { setCategoryMeta, startCategoryPickers } from './ui/category-picker.js';
import { claimDevice, wipeDevice } from './adapters/device.js';
import { setCurrentUser, currentUser } from './adapters/session.js';

type State = E.EditState & { base: Loaded };
let state: State | null = null;
let source: WorkbookSource | null = null;
let fileId = '';
let flushing = false;

/** Sitio de demostración (personal-finances-demo…): solo enseña los datos inventados, sin servidor ni inicio de sesión. */
const DEMO_SITE = import.meta.env.VITE_DEMO_SITE === '1';
const isDemo = () => { try { localStorage.removeItem('pf-demo'); } catch { /* sin almacenamiento */ } return DEMO_SITE || new URLSearchParams(location.search).has('demo'); };

function defaultMonth(months: string[]): string {
  const now = todayIso().slice(0, 7);
  return months.find(m => m <= now) ?? months[0] ?? now;
}

/** Datos del libro + los cambios pendientes aplicados encima (lo que ve el usuario). */
async function apply(loaded: Loaded) {
  const pending = source && source.id !== 'demo' ? await queue.list(source.id) : [];
  const wb = pending.filter(p => p.status === 'pending').reduce((w, p) => applyLocally(w, p.cmd), loaded.wb);
  const shown: Loaded = wb === loaded.wb ? loaded : { ...loaded, wb, health: checkWorkbook(wb, {}) };
  const c = compute(wb);
  const month = state?.month ?? defaultMonth(c.months);
  state = { loaded: shown, base: loaded, c, month, syncing: state?.syncing ?? false, syncError: state?.syncError ?? '', demo: source?.id === 'demo', pending };
  setCategoryMeta(wb.categories, recentCategories(wb));
}

async function refresh(force: boolean) {
  if (!source || !state) return;
  state.syncing = true; state.syncError = '';
  document.body.classList.add('busy');
  try {
    const fresh = await sync(source, indexedDbCache, force);
    await apply(fresh ?? { ...state.base, syncedAt: new Date().toISOString() });
    void autoSnapshots();
  } catch (err) {
    state.syncError = `No se pudo sincronizar: ${(err as Error).message}`;
  } finally {
    state.syncing = false;
    document.body.classList.remove('busy');
    render();
  }
}

/**
 * Fotos mensuales de patrimonio: una vez por sesión, tras leer el libro, se guardan las de los meses cerrados
 * que falten (nunca pisan una existente). Solo con el libro sano y fuera de la demostración.
 */
let snapshotsChecked = false;
async function autoSnapshots() {
  if (snapshotsChecked || !state || !source || source.id === 'demo' || simDay || !state.base.health.ok) return;
  snapshotsChecked = true;
  const queued = new Set(state.pending.flatMap(p => (p.cmd.type === 'upsert-snapshot' ? [p.cmd.snapshot.month] : [])));
  const missing = missingSnapshots(compute(state.base.wb), todayIso()).filter(x => !queued.has(x.month));
  if (missing.length) await editor.edit(missing.map(snapshot => ({ type: 'upsert-snapshot' as const, snapshot, onlyIfMissing: true })), 'Foto mensual del patrimonio');
}

/** Escribe en el libro los cambios pendientes; si alguno se guardó, vuelve a leer el libro. */
async function flush() {
  if (!source || source.id === 'demo' || flushing || !navigator.onLine) return;
  flushing = true;
  try {
    const r = await processQueue(source.id, sheetsApi(fileId, () => api.accessToken()), { tricount: tricountApi() });
    if (r.stoppedBy && state) state.syncError = `Cambios pendientes: ${r.stoppedBy}`;
    // Tras escribir, se vuelve a leer el libro SIEMPRE (Drive tarda unos segundos en cambiar su fecha de modificación)
    if (r.done || r.conflicts) await refresh(true);
    else if (state) { await apply(state.base); render(); }
  } finally { flushing = false; }
}

/**
 * Simular otro día (solo DEV y en local, nunca en PROD): ?hoy=AAAA-MM-DD hace que la app crea que es ese día, para
 * probar el cambio de año. Es solo para mirar: nada se guarda en el libro (llevaría fechas que aún no han llegado).
 */
const simHost = /-dev\.|^localhost$|^127\.0\.0\.1$/.test(location.hostname);
const simDay = (() => { const d = new URLSearchParams(location.search).get('hoy') ?? ''; return simHost && /^\d{4}-\d{2}-\d{2}$/.test(d) && !Number.isNaN(Date.parse(d)) ? d : ''; })();

const editor: E.Editor = {
  async edit(cmds: Command[], label: string) {
    if (!state || !cmds.length) return;
    if (source?.id === 'demo' || simDay) {
      // Demostración: el cambio se ve, pero no se guarda en ningún sitio
      const b = { ...state.base, wb: cmds.reduce(applyLocally, state.base.wb) };
      await apply(b);
      state.syncError = simDay ? 'Simulación: los cambios no se guardan' : 'Demostración: los cambios no se guardan';
      render();
      return;
    }
    for (const cmd of cmds) await queue.add(source!.id, cmd, label);
    await apply(state.base);
    render();
    void flush();
  },
  async dismiss(key: number) {
    await queue.remove(key);
    await refresh(true);
  },
  retry: () => flush(),
};

function render() {
  if (!state) return;
  const s = state;
  const parts = (location.hash.replace(/^#\/?/, '') || '').split('/').map(decodeURIComponent);
  clearChrome();
  document.body.classList.remove('wide', 'own-scroll');
  try {
    switch (parts[0]) {
      case '': V.viewHome(s); fab('#/nuevo', 'Añadir movimiento'); break;
      case 'categorias': if (/^\d{4}-\d{2}$/.test(parts[1] ?? '')) s.month = parts[1]!; V.viewCategories(s); break;
      case 'ano': if (/^\d{4}$/.test(parts[1] ?? '')) s.month = `${parts[1]}-${parts[1] === todayIso().slice(0, 4) ? todayIso().slice(5, 7) : '12'}`; document.body.classList.add('wide'); Y.viewYear(s); break;
      case 'categoria': if (/^\d{4}-\d{2}$/.test(parts[2] ?? '')) s.month = parts[2]!; V.viewCategory(s, parts[1] ?? '', editor); break;
      case 'movimientos': V.viewMovements(s); fab('#/nuevo', 'Añadir movimiento'); break;
      case 'mov': return E.viewMovementEdit(s, parts[1] ?? '', editor);
      case 'enlazar': return E.viewLink(s, parts[1] ?? '', editor);
      case 'dividir': return E.viewSplit(s, parts[1] ?? '', editor);
      case 'nuevo': return E.viewNew(s, editor);
      case 'nuevo-carga': {
        // Desde el aviso de Inicio: la carga de esa cuenta, con los datos de la anterior (el día 1 de este mes, y su mes en el concepto)
        const prev = lastLoad(s.c.wb.movements, parts[1] ?? '');
        const date = `${todayIso().slice(0, 7)}-01`;
        return E.viewNew(s, editor, { account: parts[1] ?? '', income: true, amount: prev?.amount ?? null, concept: prev ? loadConcept(prev.concept, date) : 'Carga',
          category: prev?.category || (s.c.wb.categories.some(c => c.name === 'Carga') ? 'Carga' : ''), date });
      }
      case 'presupuesto': return E.viewBudget(s);
      case 'presupuesto-categoria': return E.viewBudgetCategory(s, parts[1] ?? '', parts[2] ?? '', editor);
      case 'presupuesto-anual': document.body.classList.add('wide', 'own-scroll'); return viewPlanner(s, parts[1] ?? '', editor);
      case 'planificar': return viewPlannerWizard(s, parts[1] ?? '', editor);
      case 'cuenta': return A.viewAccount(s, parts[1] ?? '', editor, parts[2] === 'todos');
      case 'cuenta-editar': return A.viewAccountForm(s, parts[1] ?? '', editor);
      case 'cuenta-nueva': return A.viewAccountForm(s, null, editor);
      case 'patrimonio': V.viewNetWorth(s); break;
      case 'patrimonio-mes': return I.viewSnapshotEdit(s, parts[1] ?? '', editor);
      case 'inversiones': return I.viewInvestments(s, editor);
      case 'tricount': return T.viewTricount(s, editor, parts[1] === 'quitar' ? parts[2] ?? '' : '');
      case 'importar': return M.viewImport(s, parts[1] ?? '');
      case 'importar-revisar': return M.viewImportReview(s, editor);
      // «Compartidos al importar» ahora está en cada categoría (Más → Categorías)
      case 'importar-compartidos': location.replace('#/categorias-admin'); return;
      case 'categorias-admin': return C.viewCategoryAdmin(s, editor);
      case 'previstos': return P.viewPlannedYear(s, parts[1] ?? '', editor);
      case 'categoria-editar': return C.viewCategoryEdit(s, parts[1] ?? '', editor);
      case 'bancos': return B.viewBanks(s, editor);
      case 'atajos': return S.viewShortcuts(s);
      case 'amazon': return viewAmazon(s, editor);
      case 'amazon-cobro': return viewAmazonCharge(s, parts[1] ?? '', editor);
      case 'usuarios': return U.viewUsers(s);
      case 'libro': return void changeWorkbook();
      case 'tricount-saldar': return viewTricountSettle(s, editor);
      case 'tricount-enviar': return T.viewTricountSend(s, parts[1] ?? '', editor);
      case 'buscar': return V.viewSearch(s, parts[1] ?? '', editor);
      case 'estado': V.viewHealth(s); break;
      case 'mas': V.viewMore(s); break;
      case 'resumen-secciones': return V.viewHomeLayout(s);
      default: location.hash = '#/'; return;
    }
    if (s.pending.length) $('#view').insertAdjacentHTML('afterbegin', E.pendingBanner(s));
  } catch (err) {
    V.setView(`<div class="error">No se pudo mostrar: ${esc((err as Error).message)}</div>`);
  }
}

/** Aviso fijo arriba cuando Google retira el permiso: qué pasa y el botón para darlo otra vez. */
addEventListener('pf-reauth', () => {
  if (document.getElementById('reauthBar')) return;
  const bar = document.createElement('div');
  bar.id = 'reauthBar';
  bar.className = 'reauth-bar';
  bar.innerHTML = `<span><b>Google necesita que vuelvas a dar permiso</b> a la app para leer y guardar en tus libros (pasa si lo retiraste, cambiaste la contraseña o caducó). Tus datos están bien.</span>
    <a class="btn small" href="/api/auth/google/start">Volver a dar permiso</a>`;
  document.body.prepend(bar);
});

function welcome(error = '') {
  $('#title').textContent = 'Finanzas';
  $('#refreshBtn').hidden = true;
  V.setView(`
    <section class="card"><h2>Finanzas</h2>
      <div class="sub">Tus finanzas desde tu propio libro de Google Sheets. La app guarda una copia en este dispositivo para abrir al instante; el libro sigue siendo la única verdad.</div>
      ${error ? `<div class="error" style="margin-top:10px">${esc(error)}</div>` : ''}
      <div class="actions"><a class="btn" href="/api/auth/google/start">Entrar con Google</a><button class="btn secondary" id="demo">Ver la demostración</button></div>
      <div class="sub hint">Solo pedimos tu email y acceso a los ficheros que tú elijas o que cree la app (nada más de tu Drive). <a href="/privacidad/">Privacidad</a></div>
    </section>`);
  $('#demo').onclick = () => { location.search = '?demo'; };
}

/** «Cambiar de libro» (Más): otro libro, o uno nuevo. Al terminar se vuelve a abrir la app con él. */
async function changeWorkbook() {
  const me = currentUser();
  if (!me?.email || source?.id === 'demo') { location.hash = '#/mas'; return; }
  if (state?.pending.length && !confirm(`Hay ${state.pending.length} cambios sin guardar en el libro actual que se perderán. ¿Seguir?`)) { location.hash = '#/mas'; return; }
  const id = await O.chooseOrCreateWorkbook({ email: me.email, name: me.displayName, canCancel: true });
  if (!id) { location.hash = '#/mas'; return; }
  await indexedDbCache.clear();
  try { localStorage.setItem('pf-last-workbook', id); } catch { /* sin almacenamiento */ }
  location.hash = '#/';
  location.reload();
}

async function repair(issue: HealthIssue) {
  if (!state || !issue.repair) return;
  const r = issue.repair;
  if (r.kind === 'rewrite-formulas') await editor.edit([{ type: 'rewrite-formulas', cells: r.cells }], 'Reparar fórmulas');
  if (r.kind === 'add-notes-tab') await editor.edit([{ type: 'add-notes-tab' }], 'Crear la pestaña Notas');
  if (r.kind === 'compact-movements') await editor.edit([{ type: 'compact-movements' }], 'Subir movimientos debajo de filas vacías');
  if (r.kind === 'clear-orphan-ids') {
    await editor.edit(state.base.wb.orphanIds.filter(o => r.rows.includes(o.row)).map(o => ({ type: 'clear-orphan-id' as const, row: o.row, id: o.id })), 'Limpiar Ids en filas vacías');
  }
  if (r.kind === 'assign-ids') {
    await editor.edit(state.base.wb.movements.filter(m => !m.id && r.rows.includes(m.row))
      .map(m => ({ type: 'assign-id' as const, row: m.row, fingerprint: m.fingerprint, id: newId() })), 'Asignar Ids a filas añadidas a mano');
  }
}

/**
 * Quita de la cola las órdenes "asignar Id" a filas que no son movimientos (un error de una versión anterior
 * las creaba para las filas vacías con casilla). El ejecutor ya las ignora; así además no se ven como pendientes.
 */
async function purgeInvalid(loaded: Loaded) {
  if (!source || source.id === 'demo') return;
  const valid = new Set(loaded.wb.movements.filter(m => !m.id).map(m => m.row));
  for (const q of await queue.list(source.id)) {
    if (q.cmd.type === 'assign-id' && !valid.has(q.cmd.row)) await queue.remove(q.key!);
  }
}

async function start() {
  $('#refreshBtn').onclick = () => (state?.pending.length ? flush() : refresh(true));
  // En pantallas con el mes en la dirección (una categoría), se cambia la dirección; si no, el mes se quedaría fijo
  const goMonth = (m: string | undefined) => {
    if (!state || !m) return;
    state.month = m;
    const parts = location.hash.split('/');
    if (parts[1] === 'categoria' && parts[2]) location.hash = `#/categoria/${parts[2]}/${m}`;
    else if (parts[1] === 'categorias' && parts[2]) location.hash = `#/categorias/${m}`;
    else render();
  };
  $('#prevMonth').onclick = () => { if (state) goMonth(V.monthsAround(state, -1)); };
  $('#nextMonth').onclick = () => { if (state) goMonth(V.monthsAround(state, 1)); };
  $('#backBtn').onclick = () => (history.length > 1 ? history.back() : (location.hash = '#/'));
  startCategoryPickers($('#view'));
  window.addEventListener('hashchange', render);
  window.addEventListener('online', () => { void flush(); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible' && state && source?.id !== 'demo') { void flush().then(() => refresh(false)); } });
  $('#view').addEventListener('click', async e => {
    const t = e.target as HTMLElement;
    if (t.dataset.dismiss) return editor.dismiss(Number(t.dataset.dismiss));
    if (t.id === 'retryQueue') return flush();
    if (t.dataset.repair && state) return repair([...state.loaded.health.blocking, ...state.loaded.health.warnings][Number(t.dataset.repair)]!);
    if (t.id !== 'wipe') return;
    if (state?.pending.length && !confirm(`Hay ${state.pending.length} cambios sin guardar en el libro que se perderán. ¿Seguir?`)) return;
    if (!confirm('¿Borrar la copia de este dispositivo? El libro no se toca.')) return;
    await wipeDevice();
    if (source && source.id !== 'demo') await api.logout().catch(() => { /* la sesión ya no existe */ });
    location.href = '/';
  });

  if (isDemo()) {
    const demo = demoSource();
    source = demo;
    // Nada de lo real: ni el servidor (tu sesión), ni tu libro de Tricount; y «hoy» es el día de los datos inventados
    blockApi('En la demostración no se conecta con nada real');
    hideTricountBook();
    setFixedToday(await demo.today().catch(() => ''));
    const bar = document.createElement('div');
    bar.className = 'demo-bar';
    bar.innerHTML = `Demostración con datos inventados · los cambios no se guardan · <a href="/">${DEMO_SITE ? 'Empezar de nuevo' : 'Salir'}</a>`;
    document.body.prepend(bar);
  } else {
    const params = new URLSearchParams(location.search);
    if (params.has('login_error')) { history.replaceState(null, '', '/'); return welcome(params.get('login_error')!); }
    let me;
    try { me = await api.me(); }
    catch (err) {
      if (err instanceof ApiError && err.status === 401) return welcome();
      const last = (() => { try { return localStorage.getItem('pf-last-workbook'); } catch { return null; } })();
      if (!last) return welcome('No hay conexión y este dispositivo aún no tiene copia del libro');
      me = { email: '', displayName: '', workbooks: [{ kind: 'finance' as const, fileId: last, name: '' }] };
    }
    // Lo de otra cuenta que hubiera en este dispositivo se borra antes de nada
    if (me.email) { setCurrentUser(me); await claimDevice(me.email); }
    adoptServerPrefs(me.prefs);
    if (me.email) { const t = me.workbooks.find(w => w.kind === 'tricount'); setTricountBook(t ? { fileId: t.fileId, name: t.name } : null); }
    fileId = me.workbooks.find(w => w.kind === 'finance')?.fileId ?? await O.chooseOrCreateWorkbook({ email: me.email, name: me.displayName, canCancel: false }) ?? '';
    if (!fileId) return;
    try { localStorage.setItem('pf-last-workbook', fileId); } catch { /* sin almacenamiento */ }
    $('#refreshBtn').hidden = false;
    source = sheetsSource(fileId, () => api.accessToken());
  }
  if (simDay) {
    setFixedToday(simDay);
    const bar = document.createElement('div');
    bar.className = 'demo-bar sim-bar';
    bar.innerHTML = `Simulando el ${esc(new Date(`${simDay}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }))} · solo para mirar: no se guarda nada · <a href="${esc(location.pathname)}">Volver a hoy</a>`;
    document.body.prepend(bar);
  }
  // 1) Lo que haya en el dispositivo, al instante. 2) El libro y los cambios pendientes, en segundo plano.
  // (la demostración no: se carga siempre recién publicada, sin copias de otras visitas)
  const cached = source.id === 'demo' ? null : await loadCached(source, indexedDbCache);
  if (cached) await purgeInvalid(cached);
  if (cached) { await apply(cached); render(); await flush(); void refresh(false); return; }
  V.setView('<div class="loading">Descargando el libro…</div>');
  try {
    await apply((await sync(source, indexedDbCache, true))!);
    render();
    void flush();
    void autoSnapshots();
  } catch (err) {
    V.setView(`<div class="error">No se pudo cargar el libro: ${esc((err as Error).message)}</div>`);
  }
}

void start();
if ('serviceWorker' in navigator && import.meta.env.PROD) navigator.serviceWorker.register('/sw.js').catch(() => { /* sin modo sin conexión */ });
